import React from "react"
import { ControlPosition, MapControl, useMap } from "@vis.gl/react-google-maps"
import { Box, IconButton } from "@chakra-ui/react"
import { FaPlus, FaMinus } from "react-icons/fa"
import { useGoogleMapsEffect } from "../lib/hooks/useGoogleMapsEffect"

interface GoogleMapsZoomControlProps {
  mapId: string
  position: ControlPosition
}

const ZoomControl: React.FC<GoogleMapsZoomControlProps> = ({ mapId, position }) => {
  const map = useMap(mapId)
  const [zoom, setZoom] = React.useState<number | undefined>()

  useGoogleMapsEffect(
    mapId,
    () => {
      if (!zoom) return
      map?.setZoom(zoom)
    },
    [zoom],
  )

  useGoogleMapsEffect(
    mapId,
    () => {
      // once fit bounds is finished, set initial zoom value based on bounds
      const listener = google.maps.event.addListenerOnce(map!, "idle", () => {
        const initialZoom = map?.getZoom()
        setZoom(initialZoom)
      })
      return () => {
        listener.remove()
      }
    },
    [],
  )

  // init zoom
  const zoomIn = React.useCallback(() => setZoom((prev) => Math.min(18, (prev ?? 0) + 1)), [setZoom])
  const zoomOut = React.useCallback(() => setZoom((prev) => Math.max(1, (prev ?? 0) - 1)), [setZoom])

  return (
    <MapControl position={position}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        m={3}
        shadow="rgba(0, 0, 0, 0.3) 0px 1px 4px"
      >
        <IconButton
          size="sm"
          borderTopRadius={3}
          borderBottomRadius={0}
          borderBottom="1px solid rgba(0, 0, 0, 0.1)"
          aria-label="zoom in"
          icon={<FaPlus />}
          onClick={zoomIn}
        />
        <IconButton
          size="sm"
          borderTopRadius={0}
          borderBottomRadius={3}
          borderBottom="1px solid rgba(0, 0, 0, 0.1)"
          aria-label="zoom out"
          icon={<FaMinus />}
          onClick={zoomOut}
        />
      </Box>
    </MapControl>
  )
}

export default ZoomControl
